import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Save, Trash2, Tag, Settings, Users, FileText, Search, Eye, Gift } from "lucide-react";
import Logo from "@/components/Logo";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function SaCmsPage() {
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [basePrice, setBasePrice] = useState("");
    const [proPrice, setProPrice] = useState("");
    const [promoForms, setPromoForms] = useState({
        base: { promo_price: "", promo_starts_at: "", promo_ends_at: "" },
        pro: { promo_price: "", promo_starts_at: "", promo_ends_at: "" },
    });
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);

    const loadUsers = async () => {
        setUsersLoading(true);
        try {
            const { data } = await api.get("/admin/cms/users");
            setUsers(data.items);
        } catch (e) {
            if (e.response?.status === 401) {
                navigate("/sa-login");
                return;
            }
        } finally {
            setUsersLoading(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/cms/pricing");
            setConfig(data);
            setBasePrice(String(data.plans.base.price));
            setProPrice(String(data.plans.pro.price));
            setPromoForms({
                base: {
                    promo_price: data.plans.base.promo_price ?? "",
                    promo_starts_at: data.plans.base.promo_starts_at?.slice(0, 16) || "",
                    promo_ends_at: data.plans.base.promo_ends_at?.slice(0, 16) || "",
                },
                pro: {
                    promo_price: data.plans.pro.promo_price ?? "",
                    promo_starts_at: data.plans.pro.promo_starts_at?.slice(0, 16) || "",
                    promo_ends_at: data.plans.pro.promo_ends_at?.slice(0, 16) || "",
                },
            });
        } catch (e) {
            if (e.response?.status === 401) {
                navigate("/sa-login");
                return;
            }
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // eslint-disable-next-line
        load();
        // eslint-disable-next-line
        loadUsers();
    }, []);

    const savePrices = async () => {
        try {
            await api.put("/admin/cms/pricing", {
                base_price: parseFloat(basePrice),
                pro_price: parseFloat(proPrice),
            });
            toast.success("Prezzi base aggiornati");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const savePromo = async (plan) => {
        const f = promoForms[plan];
        if (!f.promo_price || !f.promo_ends_at) {
            toast.error("Inserisci prezzo promo e data di fine");
            return;
        }
        try {
            await api.post("/admin/cms/promo", {
                plan,
                promo_price: parseFloat(f.promo_price),
                promo_starts_at: f.promo_starts_at
                    ? new Date(f.promo_starts_at).toISOString()
                    : null,
                promo_ends_at: new Date(f.promo_ends_at).toISOString(),
            });
            toast.success(`Promo ${plan.toUpperCase()} attivata`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const removePromo = async (plan) => {
        if (!confirm(`Rimuovere la promo dal piano ${plan.toUpperCase()}?`)) return;
        try {
            await api.delete(`/admin/cms/promo/${plan}`);
            toast.success("Promo rimossa");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const logout = async () => {
        try {
            await api.post("/admin/logout");
        } catch (_) {
            /* ignore */
        }
        try {
            localStorage.removeItem("sf_admin_token");
        } catch (_) {
            /* ignore */
        }
        navigate("/sa-login");
    };

    return (
        <div className="min-h-screen bg-sf-bg" data-testid="sa-cms-page">
            <header className="bg-navy text-white">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <Logo className="h-12 w-auto bg-white rounded-md p-1" />
                        </Link>
                        <div>
                            <div className="label-eyebrow text-amber-brand">
                                Staff Flow Admin
                            </div>
                            <div className="font-display text-xl">CMS prezzi & promo</div>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        className="border-white text-white hover:bg-white/10"
                        onClick={logout}
                        data-testid="sa-cms-logout"
                    >
                        <LogOut size={14} className="mr-2" /> Esci
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-12">
                {loading && (
                    <div className="text-sf-muted">Caricamento…</div>
                )}

                {!loading && config && (
                    <>
                        {/* Base prices */}
                        <section
                            className="bg-white border border-sf rounded-2xl p-8 mb-8"
                            data-testid="sa-cms-prices"
                        >
                            <div className="flex items-start gap-4 mb-6">
                                <div className="w-12 h-12 rounded-lg bg-navy text-amber-brand flex items-center justify-center">
                                    <Settings size={22} />
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-1">
                                        Prezzi base
                                    </div>
                                    <h2 className="font-display text-2xl text-navy">
                                        Listino mensile
                                    </h2>
                                    <p className="text-sf-secondary text-sm mt-1">
                                        Modifica i prezzi mensili dei piani.
                                        Per le promozioni a tempo, usa la
                                        sezione sotto.
                                    </p>
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <Label>Prezzo Base (€/mese)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={basePrice}
                                        onChange={(e) => setBasePrice(e.target.value)}
                                        className="mt-2"
                                        data-testid="sa-cms-base-price"
                                    />
                                </div>
                                <div>
                                    <Label>Prezzo Pro (€/mese)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={proPrice}
                                        onChange={(e) => setProPrice(e.target.value)}
                                        className="mt-2"
                                        data-testid="sa-cms-pro-price"
                                    />
                                </div>
                            </div>
                            <Button
                                className="bg-navy text-white mt-6"
                                onClick={savePrices}
                                data-testid="sa-cms-save-prices"
                            >
                                <Save size={14} className="mr-2" /> Salva prezzi
                            </Button>
                        </section>

                        {/* Promos */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {["base", "pro"].map((plan) => {
                                const cfg = config.plans[plan];
                                const live = config.live[plan];
                                const promoActive = live.promo_active;
                                return (
                                    <section
                                        key={plan}
                                        className={`rounded-2xl p-8 border ${promoActive ? "bg-amber-brand/10 border-amber-brand" : "bg-white border-sf"}`}
                                        data-testid={`sa-cms-promo-${plan}`}
                                    >
                                        <div className="flex items-start gap-3 mb-5">
                                            <div className="w-10 h-10 rounded-lg bg-amber-brand text-navy flex items-center justify-center">
                                                <Tag size={18} />
                                            </div>
                                            <div>
                                                <div className="label-eyebrow">
                                                    Promo {plan.toUpperCase()}
                                                </div>
                                                <h3 className="font-display text-xl text-navy">
                                                    Sconto a tempo limitato
                                                </h3>
                                            </div>
                                        </div>

                                        {promoActive ? (
                                            <div className="bg-white border border-sf rounded-lg p-4 mb-5">
                                                <div className="flex items-baseline gap-3 mb-2">
                                                    <span className="font-display text-3xl text-amber-brand">
                                                        €{live.promo_price?.toFixed(2)}
                                                    </span>
                                                    <span className="line-through text-sf-muted">
                                                        €{live.price.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-sf-secondary">
                                                    Attiva fino al{" "}
                                                    <strong className="text-navy">
                                                        {fmtDate(live.promo_ends_at)}
                                                    </strong>
                                                </div>
                                            </div>
                                        ) : cfg.promo_price ? (
                                            <div className="bg-sf-surface-muted border border-sf rounded-lg p-4 mb-5 text-sf-muted text-sm">
                                                Promo configurata ma{" "}
                                                <strong>non attiva</strong>{" "}
                                                (fuori finestra temporale).
                                            </div>
                                        ) : (
                                            <div className="bg-sf-surface-muted border border-sf rounded-lg p-4 mb-5 text-sf-muted text-sm">
                                                Nessuna promo configurata.
                                            </div>
                                        )}

                                        <div className="space-y-3">
                                            <div>
                                                <Label>Prezzo promo (€/mese)</Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={promoForms[plan].promo_price}
                                                    onChange={(e) =>
                                                        setPromoForms({
                                                            ...promoForms,
                                                            [plan]: {
                                                                ...promoForms[plan],
                                                                promo_price: e.target.value,
                                                            },
                                                        })
                                                    }
                                                    className="mt-2"
                                                    data-testid={`sa-cms-promo-${plan}-price`}
                                                />
                                            </div>
                                            <div>
                                                <Label>
                                                    Inizio (opzionale)
                                                </Label>
                                                <Input
                                                    type="datetime-local"
                                                    value={
                                                        promoForms[plan].promo_starts_at
                                                    }
                                                    onChange={(e) =>
                                                        setPromoForms({
                                                            ...promoForms,
                                                            [plan]: {
                                                                ...promoForms[plan],
                                                                promo_starts_at:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    className="mt-2"
                                                    data-testid={`sa-cms-promo-${plan}-start`}
                                                />
                                            </div>
                                            <div>
                                                <Label>Fine (obbligatoria)</Label>
                                                <Input
                                                    type="datetime-local"
                                                    value={
                                                        promoForms[plan].promo_ends_at
                                                    }
                                                    onChange={(e) =>
                                                        setPromoForms({
                                                            ...promoForms,
                                                            [plan]: {
                                                                ...promoForms[plan],
                                                                promo_ends_at:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    className="mt-2"
                                                    data-testid={`sa-cms-promo-${plan}-end`}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2 mt-5">
                                            <Button
                                                className="bg-navy text-white flex-1"
                                                onClick={() => savePromo(plan)}
                                                data-testid={`sa-cms-promo-${plan}-save`}
                                            >
                                                <Save size={14} className="mr-2" />{" "}
                                                {cfg.promo_price ? "Aggiorna" : "Attiva"}{" "}
                                                promo
                                            </Button>
                                            {cfg.promo_price && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => removePromo(plan)}
                                                    data-testid={`sa-cms-promo-${plan}-delete`}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            )}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>

                        <div className="mt-10 text-xs text-sf-muted">
                            Staff Flow 360 è un prodotto di Makai · P.IVA
                            09364410969
                        </div>
                    </>
                )}

                {/* USERS & BILLING DATA */}
                <section
                    className="bg-white border border-sf rounded-2xl p-8 mt-10"
                    data-testid="sa-cms-users"
                >
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-navy text-amber-brand flex items-center justify-center flex-shrink-0">
                                <Users size={22} />
                            </div>
                            <div>
                                <div className="label-eyebrow mb-1">
                                    Utenti registrati
                                </div>
                                <h2 className="font-display text-2xl text-navy">
                                    Anagrafica clienti & fatturazione
                                </h2>
                                <p className="text-sf-secondary text-sm mt-1">
                                    {users.length} utenti totali. Click su una
                                    riga per vedere tutti i dati di fatturazione.
                                </p>
                            </div>
                        </div>
                        <div className="relative w-full md:w-64">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-sf-muted"
                            />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Cerca per email, nome, P.IVA…"
                                className="pl-9"
                                data-testid="sa-cms-users-search"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-sf rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-[#fafaf7]">
                                <tr className="text-left">
                                    <th className="px-4 py-3 label-eyebrow">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        Nome
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        Ragione sociale
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        P.IVA / CF
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        Piano
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        Stato
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow">
                                        Registrato
                                    </th>
                                    <th className="px-4 py-3 label-eyebrow"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersLoading && (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-4 py-8 text-center text-sf-muted"
                                        >
                                            Caricamento…
                                        </td>
                                    </tr>
                                )}
                                {!usersLoading && users.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-4 py-12 text-center text-sf-muted"
                                        >
                                            Nessun utente registrato.
                                        </td>
                                    </tr>
                                )}
                                {users
                                    .filter((u) => {
                                        const q = search.toLowerCase().trim();
                                        if (!q) return true;
                                        const bi = u.billing_info || {};
                                        return (
                                            (u.email || "").toLowerCase().includes(q) ||
                                            (u.name || "").toLowerCase().includes(q) ||
                                            (bi.company_name || "").toLowerCase().includes(q) ||
                                            (bi.tax_id || "").toLowerCase().includes(q) ||
                                            (bi.email || "").toLowerCase().includes(q)
                                        );
                                    })
                                    .map((u) => (
                                        <tr
                                            key={u.id}
                                            className="border-t border-sf hover:bg-[#fafaf7] cursor-pointer"
                                            onClick={() => setSelectedUser(u)}
                                            data-testid={`sa-cms-user-row-${u.id}`}
                                        >
                                            <td className="px-4 py-3 font-medium text-navy">
                                                {u.email}
                                            </td>
                                            <td className="px-4 py-3">
                                                {u.name || "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                {u.billing_info?.company_name || (
                                                    <span className="text-sf-muted italic">
                                                        non inseriti
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs">
                                                {u.billing_info?.tax_id || "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="capitalize text-xs px-2 py-1 rounded bg-[#f0efeb] text-navy font-semibold">
                                                    {u.subscription_tier === "free"
                                                        ? "Trial"
                                                        : u.subscription_tier}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge user={u} />
                                            </td>
                                            <td className="px-4 py-3 text-xs text-sf-muted font-mono">
                                                {u.created_at
                                                    ? new Date(u.created_at).toLocaleDateString("it-IT", {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                      })
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedUser(u);
                                                    }}
                                                    className="text-amber-brand hover:text-navy p-1"
                                                    data-testid={`sa-cms-user-detail-${u.id}`}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <UserDetailDialog
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                    onChanged={() => {
                        loadUsers();
                    }}
                />
            </main>
        </div>
    );
}

function StatusBadge({ user }) {
    if (user.subscription_status === "active") {
        return (
            <span className="text-xs px-2 py-1 rounded bg-[#2d6a4f]/15 text-[#1d4030] font-semibold">
                Abbonato
            </span>
        );
    }
    if (user.trial_active) {
        return (
            <span className="text-xs px-2 py-1 rounded bg-amber-brand/20 text-navy font-semibold">
                Trial
            </span>
        );
    }
    if (user.trial_expired) {
        return (
            <span className="text-xs px-2 py-1 rounded bg-[#d90429]/10 text-[#a90020] font-semibold">
                Scaduto
            </span>
        );
    }
    return (
        <span className="text-xs px-2 py-1 rounded bg-[#f0efeb] text-sf-muted">
            —
        </span>
    );
}

function UserDetailDialog({ user, onClose, onChanged }) {
    const [giftPlan, setGiftPlan] = useState("pro");
    const [giftDays, setGiftDays] = useState("30");
    const [giftNote, setGiftNote] = useState("");
    const [giftBusy, setGiftBusy] = useState(false);

    if (!user) return null;
    const bi = user.billing_info;

    const giftPlanToUser = async () => {
        const days = parseInt(giftDays, 10);
        if (!days || days < 1) {
            toast.error("Inserisci una durata valida (giorni)");
            return;
        }
        setGiftBusy(true);
        try {
            await api.post(`/admin/cms/users/${user.id}/gift`, {
                plan: giftPlan,
                duration_days: days,
                note: giftNote || null,
            });
            toast.success(
                `Piano ${giftPlan.toUpperCase()} regalato per ${days} giorni`
            );
            onChanged && onChanged();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setGiftBusy(false);
        }
    };

    const revokeGift = async () => {
        if (!confirm("Revocare l'abbonamento regalato e riportare l'utente al trial?")) return;
        setGiftBusy(true);
        try {
            await api.delete(`/admin/cms/users/${user.id}/gift`);
            toast.success("Abbonamento revocato");
            onChanged && onChanged();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setGiftBusy(false);
        }
    };

    return (
        <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-xl max-h-[90vh] overflow-y-auto"
                data-testid="sa-cms-user-detail-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy">
                        {user.name || user.email}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 text-sm">
                    <Section title="Account">
                        <Row label="Email account" value={user.email} mono />
                        <Row label="Nome" value={user.name || "—"} />
                        <Row label="Ruolo" value={user.role} />
                        <Row
                            label="Provider"
                            value={user.auth_provider || "email"}
                        />
                        <Row
                            label="Registrato il"
                            value={
                                user.created_at
                                    ? new Date(user.created_at).toLocaleString("it-IT")
                                    : "—"
                            }
                        />
                    </Section>

                    <Section title="Abbonamento & trial">
                        <Row
                            label="Piano"
                            value={
                                user.subscription_tier === "free"
                                    ? "Trial"
                                    : user.subscription_tier
                            }
                        />
                        <Row label="Stato" value={
                            user.subscription_status === "active"
                                ? user.gifted_by_admin
                                    ? "Attivo (regalato)"
                                    : "Attivo"
                                : user.trial_active
                                ? `Trial in corso`
                                : user.trial_expired
                                ? "Scaduto"
                                : "Non attivo"
                        } />
                        <Row
                            label="Fine trial"
                            value={
                                user.trial_ends_at
                                    ? new Date(user.trial_ends_at).toLocaleString("it-IT")
                                    : "—"
                            }
                        />
                        {user.subscription_until && (
                            <Row
                                label="Rinnovo / fine piano"
                                value={new Date(user.subscription_until).toLocaleString("it-IT")}
                            />
                        )}
                        {user.gifted_by_admin && (
                            <>
                                <Row label="Regalato il" value={user.gifted_at ? new Date(user.gifted_at).toLocaleString("it-IT") : "—"} />
                                {user.gift_note && <Row label="Nota regalo" value={user.gift_note} />}
                            </>
                        )}
                        {user.stripe_customer_id && (
                            <Row
                                label="Stripe customer"
                                value={user.stripe_customer_id}
                                mono
                            />
                        )}
                    </Section>

                    {/* Gift plan section */}
                    <div
                        className="bg-amber-brand/10 border-2 border-amber-brand rounded-lg p-4"
                        data-testid="sa-cms-gift-section"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <Gift size={18} className="text-navy" />
                            <div className="label-eyebrow text-navy">
                                {user.gifted_by_admin
                                    ? "Abbonamento regalato attivo"
                                    : "Regala piano a questo cliente"}
                            </div>
                        </div>
                        {user.gifted_by_admin ? (
                            <>
                                <p className="text-sm text-sf-secondary mb-4">
                                    Questo cliente ha un piano{" "}
                                    <strong className="text-navy">
                                        {user.subscription_tier?.toUpperCase()}
                                    </strong>{" "}
                                    regalato dall&apos;admin, valido fino al{" "}
                                    <strong className="text-navy">
                                        {user.subscription_until
                                            ? new Date(user.subscription_until).toLocaleDateString("it-IT")
                                            : "—"}
                                    </strong>
                                    .
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={revokeGift}
                                    disabled={giftBusy}
                                    className="text-[#d90429] border-[#d90429]/40 hover:bg-[#d90429]/10"
                                    data-testid="sa-cms-revoke-gift"
                                >
                                    Revoca abbonamento regalato
                                </Button>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-sf-secondary mb-4">
                                    Attiva manualmente un piano per questo
                                    cliente senza passare da Stripe. Utile per
                                    partner, prove estese, o premi.
                                </p>
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <Label className="text-xs">Piano</Label>
                                        <Select
                                            value={giftPlan}
                                            onValueChange={setGiftPlan}
                                        >
                                            <SelectTrigger
                                                className="mt-1"
                                                data-testid="sa-cms-gift-plan"
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="base">
                                                    Base
                                                </SelectItem>
                                                <SelectItem value="pro">
                                                    Pro
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">
                                            Durata (giorni)
                                        </Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={3650}
                                            value={giftDays}
                                            onChange={(e) =>
                                                setGiftDays(e.target.value)
                                            }
                                            className="mt-1"
                                            data-testid="sa-cms-gift-days"
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <Label className="text-xs">
                                        Nota interna (opzionale)
                                    </Label>
                                    <Input
                                        value={giftNote}
                                        onChange={(e) => setGiftNote(e.target.value)}
                                        placeholder="Es. partner Renova360, premio fedeltà…"
                                        className="mt-1"
                                        data-testid="sa-cms-gift-note"
                                    />
                                </div>
                                <Button
                                    onClick={giftPlanToUser}
                                    disabled={giftBusy}
                                    className="bg-navy text-white w-full"
                                    data-testid="sa-cms-gift-confirm"
                                >
                                    <Gift size={14} className="mr-2" />
                                    {giftBusy
                                        ? "Attivazione…"
                                        : `Regala ${giftPlan.toUpperCase()} per ${giftDays || 0} giorni`}
                                </Button>
                            </>
                        )}
                    </div>

                    <Section title="Dati di fatturazione">
                        {!bi ? (
                            <div className="bg-[#fafaf7] border border-sf rounded p-3 text-sf-muted italic">
                                L&apos;utente non ha ancora inserito i dati di
                                fatturazione.
                            </div>
                        ) : (
                            <>
                                <Row
                                    label="Ragione sociale / Nome"
                                    value={bi.company_name}
                                />
                                <Row label="P.IVA / CF" value={bi.tax_id} mono />
                                <Row label="Indirizzo" value={bi.address} />
                                <Row label="Email fatturazione" value={bi.email} mono />
                                <Row label="PEC / SDI" value={bi.pec_sdi} mono />
                                <Row label="Telefono" value={bi.phone} mono />
                                {bi.updated_at && (
                                    <Row
                                        label="Aggiornato il"
                                        value={new Date(bi.updated_at).toLocaleString("it-IT")}
                                    />
                                )}
                            </>
                        )}
                    </Section>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="label-eyebrow mb-2">{title}</div>
            <div className="bg-[#fafaf7] border border-sf rounded-lg p-3 space-y-1">
                {children}
            </div>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex justify-between gap-3 py-1.5 border-b border-sf/50 last:border-b-0">
            <span className="text-sf-muted text-xs uppercase tracking-wider">
                {label}
            </span>
            <span
                className={`text-navy font-medium text-right ${mono ? "font-mono text-xs" : ""}`}
            >
                {value}
            </span>
        </div>
    );
}
